/**
 * Typography Tokens
 *
 * Text styles including font family, sizes, line heights, weights, and letter spacing.
 * Supports responsive typography with mobile and desktop scales.
 *
 * @see ../../atoms/typography/typography.mdx (the approved reference page; DS-005 is archived)
 * @figma https://www.figma.com/design/NXGWL5ErhbsA0kqoybqQbg/Design-System?node-id=6109-6037
 */

/**
 * Font family tokens
 */
export const fontFamily = {
  primary: "Inter",
  display: "Inter", // Can swap to 'Epilogue'
} as const;

/**
 * Font weight tokens
 */
export const fontWeight = {
  regular: "400",
  medium: "500",
  semibold: "600",
  bold: "700",
} as const;

/**
 * Letter spacing tokens
 */
export const letterSpacing = {
  tighter: "-0.022em", // -2.2% for display
  tight: "-0.011em", // -1.1% for label lg/md
  normal: "0", // 0 for body, heading, caption
  slight: "-0.006em", // -0.6% for label sm
} as const;

/**
 * Font sizes (mobile-first)
 */
export const fontSize = {
  display: {
    h1: 56,
    h2: 48,
    h3: 40,
  },
  heading: {
    h1: 28,
    h2: 24,
    h3: 20,
  },
  label: {
    lg: 18,
    md: 16,
    sm: 14,
  },
  body: {
    lg: 16,
    md: 14,
    sm: 12,
  },
  caption: {
    md: 10,
    sm: 8,
  },
} as const;

/**
 * Line heights (mobile-first)
 */
export const lineHeight = {
  display: {
    h1: 72,
    h2: 52,
    h3: 48,
  },
  heading: {
    h1: 36,
    h2: 32,
    h3: 28,
  },
  label: {
    lg: 26,
    md: 24,
    sm: 20,
  },
  body: {
    lg: 24,
    md: 20,
    sm: 16,
  },
  caption: {
    md: 14,
    sm: 12,
  },
} as const;

/**
 * Desktop font sizes
 */
export const fontSizeDesktop = {
  display: {
    h1: 88,
    h2: 72,
    h3: 64,
  },
  heading: {
    h1: 48,
    h2: 40,
    h3: 32,
  },
  label: {
    lg: 20,
    md: 18,
    sm: 16,
  },
  body: {
    lg: 18,
    md: 16,
    sm: 14,
  },
  caption: {
    md: 12,
    sm: 10,
  },
} as const;

/**
 * Desktop line heights
 */
export const lineHeightDesktop = {
  display: {
    h1: 96,
    h2: 80,
    h3: 72,
  },
  heading: {
    h1: 56,
    h2: 52,
    h3: 40,
  },
  label: {
    lg: 28,
    md: 26,
    sm: 24,
  },
  body: {
    lg: 26,
    md: 24,
    sm: 20,
  },
  caption: {
    md: 16,
    sm: 14,
  },
} as const;

/**
 * OTPInputField's digit, per cell size: an OTP-only size, not a step of the
 * scale above (Ardhi's DQ26 answer, molecules audit, 2026-11-02). In px, as
 * the cells are, so a digit keeps its size in its cell at any root font size
 */
export const otpDigitSize = { sm: 18, md: 24, lg: 32 } as const;

// Type exports
export type FontFamily = keyof typeof fontFamily;
export type FontWeight = keyof typeof fontWeight;
export type LetterSpacing = keyof typeof letterSpacing;
export type TextCategory = keyof typeof fontSize;
export type DisplaySize = keyof (typeof fontSize)["display"];
export type HeadingSize = keyof (typeof fontSize)["heading"];
export type LabelSize = keyof (typeof fontSize)["label"];
export type BodySize = keyof (typeof fontSize)["body"];
export type CaptionSize = keyof (typeof fontSize)["caption"];
